import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Building2, GraduationCap, Mic2, PartyPopper } from 'lucide-react';
import { cn } from '@/lib/utils';
import { LANDING_SECTION_IDS } from './constants';

const USE_CASES = [
  {
    id: 'business',
    icon: Building2,
    title: 'Local business networks',
    outcome: 'Connect local businesses, share referrals, and grow together.',
    modules: ['Business Directory', 'Lead Capture', 'Posts & Engagement'],
  },
  {
    id: 'coaching',
    icon: Mic2,
    title: 'Creator & coaching communities',
    outcome: 'Keep members engaged between sessions and turn interest into leads.',
    modules: ['Posts & Engagement', 'Events', 'Lead Capture'],
  },
  {
    id: 'associations',
    icon: GraduationCap,
    title: 'Professional associations',
    outcome: 'Run member directories, events, and announcements in one place.',
    modules: ['Community & Members', 'Events', 'Business Directory'],
  },
  {
    id: 'events',
    icon: PartyPopper,
    title: 'Event-first communities',
    outcome: 'Plan events, manage registrations, and check in attendees seamlessly.',
    modules: ['Events', 'Member engagement', 'Projects'],
  },
] as const;

export function UseCasesSection() {
  const [activeId, setActiveId] = useState<string>(USE_CASES[0].id);
  const active = USE_CASES.find((u) => u.id === activeId) ?? USE_CASES[0];

  return (
    <section id={LANDING_SECTION_IDS.useCases} className="py-24 bg-muted/30 scroll-mt-24">
      <div className="container px-6 mx-auto">
        <div className="text-center max-w-2xl mx-auto mb-12 space-y-4">
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight">
            Built for your community
          </h2>
          <p className="text-muted-foreground text-lg">
            See how different community types use Milora to achieve their goals.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
          <div className="lg:col-span-2 flex flex-col gap-2">
            {USE_CASES.map((useCase) => (
              <button
                key={useCase.id}
                type="button"
                onClick={() => setActiveId(useCase.id)}
                className={cn(
                  'flex items-center gap-3 rounded-xl border px-4 py-4 text-left transition-all',
                  activeId === useCase.id
                    ? 'border-primary/40 bg-primary/5 shadow-sm'
                    : 'border-border/50 bg-background hover:border-primary/20'
                )}
              >
                <div
                  className={cn(
                    'flex h-10 w-10 shrink-0 items-center justify-center rounded-xl',
                    activeId === useCase.id
                      ? 'bg-primary/15 text-primary'
                      : 'bg-muted text-muted-foreground'
                  )}
                >
                  <useCase.icon className="h-5 w-5" />
                </div>
                <span className="font-semibold text-foreground">{useCase.title}</span>
              </button>
            ))}
          </div>

          <div className="lg:col-span-3">
            <AnimatePresence mode="wait">
              <motion.div
                key={active.id}
                initial={{ opacity: 0, x: 12 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -12 }}
                transition={{ duration: 0.25 }}
                className="h-full rounded-2xl border border-border/50 bg-background p-8 shadow-sm"
              >
                <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-primary/20 to-primary/10 text-primary mb-6">
                  <active.icon className="h-6 w-6" />
                </div>
                <h3 className="text-2xl font-bold text-foreground">{active.title}</h3>
                <p className="mt-3 text-muted-foreground leading-relaxed">{active.outcome}</p>
                <div className="mt-8">
                  <p className="text-sm font-semibold text-foreground mb-3">Key modules used</p>
                  <div className="flex flex-wrap gap-2">
                    {active.modules.map((mod) => (
                      <span
                        key={mod}
                        className="rounded-full bg-primary/10 px-3 py-1 text-xs font-medium text-primary"
                      >
                        {mod}
                      </span>
                    ))}
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  );
}
